import React from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';

export interface NoteDisplayProps {
  content: string;
  className?: string;
  maxHeight?: string;
  showFullContent?: boolean;
}

const markdownStyles = cn(
  // Base styles
  'prose prose-sm max-w-none',
  // Text and spacing
  '[&_p]:mb-2 [&_p]:leading-relaxed [&_p]:text-sm [&_p]:text-gray-900',
  '[&_p:last-child]:mb-0',
  // Headers
  '[&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:text-gray-900',
  '[&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-gray-900',
  '[&_h3]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-gray-900',
  '[&_h4]:mb-1 [&_h4]:text-sm [&_h4]:font-medium [&_h4]:text-gray-900',
  '[&_h5]:mb-1 [&_h5]:text-sm [&_h5]:font-medium [&_h5]:text-gray-900',
  '[&_h6]:mb-1 [&_h6]:text-sm [&_h6]:font-medium [&_h6]:text-gray-900',
  // Lists
  '[&_ul]:pl-4 [&_ul]:mb-2 [&_ul]:list-disc',
  '[&_ol]:pl-4 [&_ol]:mb-2 [&_ol]:list-decimal',
  '[&_li]:mb-1 [&_li]:text-sm [&_li]:text-gray-900',
  // Links
  '[&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800',
  // Code
  '[&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono',
  '[&_pre]:bg-gray-50 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:mb-2',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  // Blockquotes
  '[&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:ml-0 [&_blockquote]:mb-2 [&_blockquote]:italic [&_blockquote]:text-gray-700',
  // Tables
  '[&_table]:border-collapse [&_table]:mb-2',
  '[&_th]:border [&_th]:border-gray-300 [&_th]:p-2 [&_th]:bg-gray-50 [&_th]:text-xs [&_th]:font-medium',
  '[&_td]:border [&_td]:border-gray-300 [&_td]:p-2 [&_td]:text-xs',
  // Horizontal rules
  '[&_hr]:border-gray-300 [&_hr]:my-3',
  // Strong and emphasis
  '[&_strong]:font-semibold [&_strong]:text-gray-900',
  '[&_em]:italic [&_em]:text-gray-800',
);

export function NoteDisplay({
  content,
  className,
  maxHeight = 'max-h-48',
  showFullContent = false,
}: NoteDisplayProps) {
  if (!content.trim()) {
    return (
      <div className={cn('text-sm text-gray-500 italic', className)}>
        Empty note
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative',
        !showFullContent && maxHeight,
        !showFullContent && 'overflow-hidden',
        className,
      )}
    >
      <div
        className={cn(markdownStyles, !showFullContent && 'overflow-y-auto')}
      >
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </div>
  );
}
